import type { BarSeriesOption } from 'echarts/charts';
import type { DatasetOption } from 'echarts/types/dist/shared';

import type { I18nService } from '@core/i18n-legacy';
import { DatasetBase } from '@core/reports-types';
import { datasetValueFormatterOptions } from '@core/reports-util';
import { valueFormatter } from '@core/utils-legacy';

import { BAR_CHART_BAR_MAX_WIDTH } from '../chart-layout';

const NO_VALUE = '-';
const STACK = 'sign';

export const POSITIVE_SERIES_ID = 'positive';
export const NEGATIVE_SERIES_ID = 'negative';
export const VALUE_COLUMN_ID = 'value';

export interface SignSplitBars {
  dataset: DatasetOption;
  series: BarSeriesOption[];
}

/**
 * Splits a single-dimension value column into two bar series — positive and
 * negative — sharing a stack so both anchor at the zero baseline. Positive
 * bars extend right, negative extend left.
 *
 * Value labels sit on the zero-baseline side of each bar (`position: 'right'`
 * for both series): positive labels land at the bar tip in the empty right
 * margin, while negative labels land at the zero baseline instead of the far
 * left tip. This keeps negative labels away from the y-axis category names /
 * left-most axis tick, which they would otherwise overlap once a bar reaches
 * the axis edge.
 *
 * A row the API reports as empty (`null`) is not a 0% one, so it is drawn as a
 * zero-length positive bar labelled with the empty marker rather than skipped
 * silently.
 *
 * The two series are always present in a fixed order so that the echarts
 * palette maps deterministically: positive -> palette[0]
 * (`colorChartPrimaryDefault`), negative -> palette[1]
 * (`colorChartPurpleDefault`). No explicit colors are set here.
 */
export const buildSignSplitBars = (dataset: DatasetBase, i18n: I18nService): SignSplitBars => {
  const dimensions = [
    dataset.dataset.dimensions[0],
    { id: POSITIVE_SERIES_ID, name: POSITIVE_SERIES_ID },
    { id: NEGATIVE_SERIES_ID, name: NEGATIVE_SERIES_ID },
    { id: VALUE_COLUMN_ID, name: VALUE_COLUMN_ID },
  ];
  const valueColumnIndex = dimensions.findIndex(({ id }) => id === VALUE_COLUMN_ID);

  const source = dataset.dataset.source.map(([category, value]) => {
    const numeric = typeof value === 'number' ? value : null;
    const isNegative = numeric !== null && numeric < 0;

    return <['string', ...Array<number | string>]>[
      category,
      isNegative ? NO_VALUE : (numeric ?? 0),
      isNegative ? numeric : NO_VALUE,
      numeric ?? NO_VALUE,
    ];
  });

  const label = () => ({
    show: true,
    position: <const>'right',
    formatter: (params: { data?: unknown }) => {
      const row = Array.isArray(params.data) ? params.data : [];

      if (row.length <= valueColumnIndex) {
        return '';
      }

      const value = row[valueColumnIndex];

      return valueFormatter(
        typeof value === 'number' ? value : null,
        datasetValueFormatterOptions(dataset),
        i18n,
      );
    },
    rich: {},
  });

  const series: BarSeriesOption[] = [
    {
      id: POSITIVE_SERIES_ID,
      type: 'bar',
      seriesLayoutBy: 'column',
      stack: STACK,
      cursor: 'default',
      barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
      label: label(),
      emphasis: { label: label() },
    },
    {
      id: NEGATIVE_SERIES_ID,
      type: 'bar',
      seriesLayoutBy: 'column',
      stack: STACK,
      cursor: 'default',
      barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
      label: label(),
      emphasis: { label: label() },
    },
  ];

  return {
    dataset: <DatasetOption>{
      dimensions,
      source,
    },
    series,
  };
};

type SignSplitTooltipParam = {
  value?: unknown;
  encode?: { x?: number[] };
};

/**
 * An axis-triggered tooltip receives one param per series, but only one of the
 * two sign series carries a real value for a given category. Collapse them to
 * that single meaningful param (keeping its series color) so the tooltip shows
 * one row instead of a duplicated pair.
 */
export const pickSignSplitParam = <T extends SignSplitTooltipParam>(
  params: T | readonly T[],
): T[] => {
  const arr = Array.isArray(params) ? [...params] : [<T>params];
  const real = arr.find(param => {
    const value = Array.isArray(param.value) ? param.value[param.encode?.x?.[0] ?? 1] : param.value;
    return typeof value === 'number';
  });
  return [real ?? arr[0]];
};
